/*
 * Shared utilities for Algolia InstantSearch sections.
 * Consumed by:
 *   - sections/algolia-instant-search.liquid
 *   - sections/algolia-custom-collections.liquid
 *   - sections/main-collection-product-grid-icons.liquid
 *
 * Exposes window.AlgoliaShared with helpers for:
 *   - currency symbol lookup
 *   - best-seller optionalFilters scoring
 *   - GA4 view_item_list impression tracking via IntersectionObserver
 *   - facets panel toggle / close / outside-click
 *   - collapsible facet panel headers
 *   - wheel scroll-propagation guard
 *   - dynamicWidgets fallbackWidget builder (size/material/color refinement lists)
 *   - horizontal "tipo de producto" filter bar (collection pages)
 */
(function () {
  'use strict';

  function getCurrencySymbol(currency) {
    switch (currency) {
      case 'USD': return '$';
      case 'GBP': return '£';
      case 'EUR':
      default:    return '€';
    }
  }

  /* Algolia indexes full-size Shopify CDN image URLs; grid cards render at
     ~200-360px, so request a resized rendition from the CDN instead.
     720px covers the worst case (2-col mobile at 3x DPR, 4-col desktop at 2x).
     Non-Shopify URLs and videos pass through untouched. */
  var GRID_IMAGE_WIDTH = 720;
  function sizedImageUrl(url, width) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/shop/') === -1) return url;
    if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) return url;
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + (width || GRID_IMAGE_WIDTH);
  }

  var bestSellerFilter = [
    'named_tags.best_seller:1<score=24>',
    'named_tags.best_seller:2<score=23>',
    'named_tags.best_seller:3<score=22>',
    'named_tags.best_seller:4<score=21>',
    'named_tags.best_seller:5<score=20>',
    'named_tags.best_seller:6<score=19>',
    'named_tags.best_seller:7<score=18>',
    'named_tags.best_seller:8<score=17>',
    'named_tags.best_seller:9<score=16>',
    'named_tags.best_seller:10<score=15>',
    'named_tags.best_seller:11<score=14>',
    'named_tags.best_seller:12<score=13>',
    'named_tags.best_seller:13<score=12>',
    'named_tags.best_seller:14<score=11>',
    'named_tags.best_seller:15<score=10>',
    'named_tags.best_seller:16<score=9>',
    'named_tags.best_seller:17<score=8>',
    'named_tags.best_seller:18<score=7>',
    'named_tags.best_seller:19<score=6>',
    'named_tags.best_seller:20<score=5>',
    'named_tags.best_seller:21<score=4>',
    'named_tags.best_seller:22<score=3>',
    'named_tags.best_seller:23<score=2>',
    'named_tags.best_seller:24<score=1>',
  ].join(' OR ');

  /**
   * GA4 `view_item_list` impression tracker.
   *
   * @param {Object}   opts
   * @param {string}   opts.currency           ISO currency code
   * @param {string}   opts.listId             item_list_id value
   * @param {string}   opts.listName           item_list_name value
   * @param {string}   opts.cardSelector       e.g. '.ais-product-card[data-handle]'
   * @param {Function} opts.getCurrentQuery    () => string  (current search query)
   * @param {Function} opts.buildItemForCard   (cardEl) => { objectID, id, title, price } | null
   * @param {boolean=} opts.debug              log instead of publishing
   * @returns {{ observeNewHits: Function, scheduleObserve: Function }}
   */
  function createImpressionTracker(opts) {
    var currency = opts.currency;
    var listId = opts.listId;
    var listName = opts.listName;
    var cardSelector = opts.cardSelector;
    var getCurrentQuery = opts.getCurrentQuery;
    var buildItemForCard = opts.buildItemForCard;
    var debug = !!opts.debug;
    var hitsContainerId = opts.hitsContainerId || 'ais-hits';

    var IMPRESSION_BATCH_DELAY_MS = 500;
    var OBSERVE_DEBOUNCE_MS = 950;
    var batchTimer = null;
    var batch = [];
    var index = 0;
    var trackedObjectIDs = {};
    var lastQuery = null;
    var debounceTimer = null;

    function flushBatch() {
      if (batch.length === 0) return;
      var items = batch.slice();
      batch = [];

      var payload = {
        currency: currency,
        item_list_id: listId,
        item_list_name: listName,
        items: items,
      };

      if (debug) {
        console.log('[view_item_list]', payload);
        return;
      }

      if (typeof window.Shopify !== 'undefined' && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('view_item_list', payload);
      }
    }

    function queueImpression(item) {
      var objectID = String(item.objectID || item.id);
      if (trackedObjectIDs[objectID]) return;
      trackedObjectIDs[objectID] = true;

      var priceRaw = parseFloat(item.price);
      var price = isNaN(priceRaw) ? 0 : priceRaw / 100;
      batch.push({
        item_id: String(item.id),
        item_name: item.title || '',
        price: parseFloat(price.toFixed(2)),
        index: index++,
      });
      if (batchTimer) clearTimeout(batchTimer);
      batchTimer = setTimeout(flushBatch, IMPRESSION_BATCH_DELAY_MS);
    }

    function reset() {
      if (batchTimer) clearTimeout(batchTimer);
      flushBatch();
      trackedObjectIDs = {};
      index = 0;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var item = entry.target.__algoliaItem;
          if (item) queueImpression(item);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    function observeNewHits() {
      var hitsContainer = document.getElementById(hitsContainerId);
      if (!hitsContainer) return;

      var currentQuery = getCurrentQuery() || '';
      if (currentQuery !== lastQuery) {
        reset();
        lastQuery = currentQuery;
      }

      var hitEls = hitsContainer.querySelectorAll(cardSelector);
      hitEls.forEach(function (el) {
        if (el.__algoliaItemAttached) return;
        var item = buildItemForCard(el);
        if (!item) return;
        el.__algoliaItem = item;
        el.__algoliaItemAttached = true;
        observer.observe(el);
      });
    }

    function scheduleObserve() {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        requestAnimationFrame(observeNewHits);
      }, OBSERVE_DEBOUNCE_MS);
    }

    return {
      observeNewHits: observeNewHits,
      scheduleObserve: scheduleObserve,
    };
  }

  /**
   * Wires up the facets panel: toggle button, close button, and click-outside.
   * Updates the toggle's text between t.viewFilters / t.hideFilters.
   */
  function setupFacetsPanel(t) {
    var facetsPanel = document.getElementById('ais-facets-panel');
    var facetsToggle = document.getElementById('ais-facets-toggle');
    var facetsClose = document.getElementById('ais-facets-close');
    if (!facetsPanel || !facetsToggle || !facetsClose) return;

    facetsToggle.addEventListener('click', function () {
      facetsPanel.classList.toggle('ais-facets__shown');
      facetsToggle.textContent = facetsPanel.classList.contains('ais-facets__shown') ? t.hideFilters : t.viewFilters;
    });

    facetsClose.addEventListener('click', function () {
      facetsPanel.classList.remove('ais-facets__shown');
      facetsToggle.textContent = t.viewFilters;
    });

    document.addEventListener('click', function (event) {
      if (!facetsPanel.classList.contains('ais-facets__shown')) return;
      if (!facetsPanel.contains(event.target) && !facetsToggle.contains(event.target)) {
        facetsPanel.classList.remove('ais-facets__shown');
        facetsToggle.textContent = t.viewFilters;
      }
    });
  }

  /** Delegated click handler that toggles `.ais-Panel--collapsed` on facet panels. */
  function setupCollapsibleFacets() {
    var facetsContentWrapper = document.querySelector('.ais-is-section .ais-facets-content-wrapper');
    if (!facetsContentWrapper) return;
    facetsContentWrapper.addEventListener('click', function (e) {
      var header = e.target.closest('.ais-Panel-header');
      if (!header) return;
      var panelEl = header.closest('.ais-Panel--collapsible');
      if (panelEl) panelEl.classList.toggle('ais-Panel--collapsed');
    });
  }

  /** Prevents wheel scroll inside the facets content wrapper from propagating to the page. */
  function setupWheelScrollPrevention() {
    document.addEventListener(
      'wheel',
      function (event) {
        var fw = document.querySelector('.ais-is-section .ais-facets-content-wrapper');
        if (!fw || !fw.contains(event.target)) return;
        var up = event.deltaY < 0;
        var down = event.deltaY > 0;
        if ((up && fw.scrollTop === 0) || (down && fw.scrollTop + fw.clientHeight >= fw.scrollHeight)) {
          event.preventDefault();
        }
      },
      { passive: false }
    );
  }

  /**
   * Builds the dynamicWidgets `fallbackWidget` factory used by both pages.
   * Handles size sort, material sort, and color swatches; columnizes 2-up grids.
   *
   * @param {Object} deps
   * @param {Function} deps.panel              instantsearch.widgets.panel
   * @param {Function} deps.refinementList     instantsearch.widgets.refinementList
   * @param {Object}   deps.t                  translation dict (for facetAttrLabels)
   * @returns {Function} fallbackWidget(opts)
   */
  function buildFallbackFacetWidget(deps) {
    var panel = deps.panel;
    var refinementList = deps.refinementList;
    var t = deps.t;

    function columnFirst(items, columns) {
      var total = items.length;
      var rows = Math.ceil(total / columns);
      var out = [];
      for (var row = 0; row < rows; row++) {
        for (var col = 0; col < columns; col++) {
          var i = col * rows + row;
          if (i < total) out.push(items[i]);
        }
      }
      return out;
    }

    return function fallbackWidget(opts) {
      var attrName = opts.attribute.split('.').pop();
      var attrKey = attrName.toLowerCase();
      var overrides = (t && t.facetAttrLabels) || {};
      var label =
        overrides[attrKey] ||
        attrName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      var isColor = attrKey === 'color';
      var isSize = attrKey === 'talla';
      var isMaterial = opts.attribute.toLowerCase() === 'myfield.material' || attrName.toLowerCase() === 'material';

      var config = {
        container: opts.container,
        attribute: opts.attribute,
        limit: 10,
        showMore: true,
        showMoreLimit: 50,
      };

      if (isSize) {
        config.limit = 100;
        config.showMore = false;
        var sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
        config.transformItems = function (items) {
          var sortedItems = items.sort(function (a, b) {
            var aUpper = a.label.toUpperCase();
            var bUpper = b.label.toUpperCase();
            var aIdx = sizeOrder.indexOf(aUpper);
            var bIdx = sizeOrder.indexOf(bUpper);
            var aNum = parseFloat(a.label);
            var bNum = parseFloat(b.label);
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
            if (aIdx !== -1) return -1;
            if (bIdx !== -1) return 1;
            if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
            return a.label.localeCompare(b.label);
          });
          return columnFirst(sortedItems, 2);
        };
      }

      if (isMaterial) {
        config.limit = 100;
        config.showMore = false;
        config.transformItems = function (items) {
          var sortedItems = items.sort(function (a, b) {
            return a.label.localeCompare(b.label);
          });
          return columnFirst(sortedItems, 2);
        };
      }

      if (isColor) {
        config.limit = 100;
        config.showMore = false;
        config.templates = {
          item: function (item) {
            var normalized = item.label.toLowerCase()
              .normalize('NFD').replace(/[̀-ͯ]/g, '')
              .replace(/\s+/g, '-');
            return '<label class="ais-RefinementList-label">' +
              '<span class="ais-color-swatch ais-color-swatch-' + normalized + '"></span>' +
              '<span class="ais-RefinementList-labelText">' + item.label + '</span>' +
            '</label>';
          },
        };
      }

      return panel({
        collapsed: function () { return true; },
        hidden: function (options) { return !options.canRefine; },
        templates: {
          header: function () { return label; },
        },
      })(refinementList)(config);
    };
  }

  /**
   * Builds the custom sort-by widget used by both pages.
   * Renders as a collapsible Panel with a UL of options (instead of a native <select>),
   * so it visually matches the refinement-list facets inside the filters panel.
   *
   * @param {Object} deps
   * @param {Object} deps.instantsearch    the global instantsearch namespace
   * @param {string} deps.container        CSS selector for the panel container (e.g. '#ais-sort-container')
   * @param {string} deps.indexName        base Algolia index name; price replicas are derived from it
   * @param {Object} deps.t                translation dict (uses t.sortBy, t.relevance, t.priceLowToHigh, t.priceHighToLow)
   * @returns {Object} an InstantSearch widget instance
   */
  function buildSortByWidget(deps) {
    var instantsearch = deps.instantsearch;
    var container = deps.container;
    var indexName = deps.indexName;
    var t = deps.t;
    var panel = instantsearch.widgets.panel;

    var sortByItems = [
      { label: t.relevance, value: indexName },
      { label: t.priceLowToHigh, value: indexName + '_price_asc' },
      { label: t.priceHighToLow, value: indexName + '_price_desc' },
    ];

    var customSortBy = instantsearch.connectors.connectSortBy(function (renderOptions) {
      var root = document.querySelector(container + ' .ais-Panel-body');
      if (!root) return;
      var options = renderOptions.options;
      var currentRefinement = renderOptions.currentRefinement;
      var refine = renderOptions.refine;

      var ul = root.querySelector('ul.ais-SortBy-list');
      if (!ul) {
        ul = document.createElement('ul');
        ul.className = 'ais-SortBy-list';
        root.innerHTML = '';
        root.appendChild(ul);
      }

      ul.innerHTML = options.map(function (option) {
        var selected = option.value === currentRefinement;
        return '<li class="ais-SortBy-item' + (selected ? ' ais-SortBy-item--selected' : '') + '"'
          + ' data-value="' + option.value + '">'
          + option.label + '</li>';
      }).join('');

      ul.querySelectorAll('.ais-SortBy-item').forEach(function (li) {
        li.addEventListener('click', function () {
          refine(li.getAttribute('data-value'));
        });
      });
    });

    return panel({
      collapsed: function () { return true; },
      templates: {
        header: function () { return t.sortBy; },
      },
    })(customSortBy)({
      container: container,
      items: sortByItems,
    });
  }

  var FILTER_BAR_ATTRIBUTE = 'meta.filtro.tipo_de_producto';

  // Last dot-segment, lowercased: meta.custom.tamano → tamano.
  function facetKeyword(attr) {
    return String(attr).split('.').pop().toLowerCase();
  }

  /**
   * Builds a dynamicWidgets transformItems that drops facets from the filters
   * drawer by keyword: the last dot-segment of the attribute, compared
   * lowercased (meta.custom.tamano → tamano). Takes the keywords hidden on the
   * collection (snippets/hidden-filters.liquid) and, when the filter bar owns
   * the tipo facet, its full attribute, which reduces to its keyword. Empty
   * entries are ignored.
   *
   * @param {string[]} hidden  keywords or full facet attributes to drop
   * @returns {Function} transformItems(attributes)
   */
  function buildFacetOmitter(hidden) {
    var hiddenKeywords = {};
    (hidden || []).forEach(function (entry) {
      if (entry) hiddenKeywords[facetKeyword(entry)] = true;
    });
    return function (attributes) {
      return attributes.filter(function (attr) {
        return !hiddenKeywords[facetKeyword(attr)];
      });
    };
  }

  /**
   * Horizontal "tipo de producto" filter bar for collection pages: a standalone
   * refinementList on the tipo facet rendered as a row of pills in the toolbar.
   *
   * The container is rendered by snippets/algolia-filter-bar.liquid only for
   * collections with custom.product_type_metaobject_filter set (unless the tipo
   * facet is hidden on the collection), with the curated
   * product_type_filter metaobject values in data-order. As on the native bar
   * (snippets/facets.liquid), only curated values are shown, in curated order.
   * A "Ver todo" pill (label in data-view-all-label) leads the bar: selected
   * while no value is refined, and clicking it un-refines them all.
   *
   * Add `attribute` to the list given to buildFacetOmitter so the facet
   * appears only in the bar, not also in the filters drawer.
   *
   * @param {Object} deps
   * @param {Object} deps.instantsearch  the global instantsearch namespace
   * @param {string} deps.container      CSS selector for the bar container (e.g. '#ais-filter-bar')
   * @returns {{ widget: Object, attribute: string }|null} null when the bar isn't rendered
   */
  function buildFilterBar(deps) {
    var container = document.querySelector(deps.container);
    if (!container) return null;

    // Mirrors Liquid's handleize so metaobject entries match facet values.
    function handleize(str) {
      return (str || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }

    var order = [];
    try {
      order = JSON.parse(container.getAttribute('data-order')) || [];
    } catch (e) {
      order = [];
    }
    var orderIndex = {};
    order.forEach(function (val, i) {
      orderIndex[handleize(val)] = i;
    });

    var viewAllLabel = container.getAttribute('data-view-all-label') || '';

    /* refine changes identity per render; the single delegated listener
       always calls the latest one (mirrors the custom sort widget). */
    var latestRefine = null;
    var latestRefined = [];
    var clickBound = false;

    var connector = deps.instantsearch.connectors.connectRefinementList(function (renderOptions) {
      latestRefine = renderOptions.refine;

      var items = renderOptions.items
        .filter(function (item) {
          return orderIndex[handleize(item.value)] !== undefined;
        })
        .sort(function (a, b) {
          return orderIndex[handleize(a.value)] - orderIndex[handleize(b.value)];
        });

      if (!items.length) {
        container.hidden = true;
        return;
      }
      container.hidden = false;
      /* All values, not only the curated ones, so a refined value the bar
         doesn't show is cleared too. */
      latestRefined = renderOptions.items.filter(function (item) {
        return item.isRefined;
      });

      var viewAllHtml =
        '<li class="ais-filter-bar__item">' +
        '<button type="button" class="ais-filter-bar__pill ais-filter-bar__pill--view-all' +
        (latestRefined.length ? '' : ' ais-filter-bar__pill--selected') +
        '" data-view-all>' +
        viewAllLabel +
        '</button></li>';

      var listHtml = viewAllHtml + items
        .map(function (item) {
          var label = item.label.charAt(0).toUpperCase() + item.label.slice(1);
          return (
            '<li class="ais-filter-bar__item">' +
            '<button type="button" class="ais-filter-bar__pill' +
            (item.isRefined ? ' ais-filter-bar__pill--selected' : '') +
            '" data-value="' +
            item.value.replace(/"/g, '&quot;') +
            '">' +
            label +
            '</button></li>'
          );
        })
        .join('');

      var ul = container.querySelector('ul.ais-filter-bar__list');
      if (!ul) {
        ul = document.createElement('ul');
        ul.className = 'ais-filter-bar__list';
        container.innerHTML = '';
        container.appendChild(ul);
      }
      if (ul.innerHTML !== listHtml) ul.innerHTML = listHtml;

      if (!clickBound) {
        clickBound = true;
        ul.addEventListener('click', function (e) {
          var pill = e.target.closest('.ais-filter-bar__pill');
          if (!pill || !latestRefine) return;
          /* Each refine toggles one value; InstantSearch defers the search,
             so un-refining several still sends a single request. */
          if (pill.hasAttribute('data-view-all')) {
            latestRefined.forEach(function (item) {
              latestRefine(item.value);
            });
          } else {
            latestRefine(pill.getAttribute('data-value'));
          }
        });
      }
    });

    return {
      widget: connector({ attribute: FILTER_BAR_ATTRIBUTE, limit: 100 }),
      attribute: FILTER_BAR_ATTRIBUTE,
    };
  }

  /* ─────────────────────────────────────────────────────────────────
     Hit-card HTML — full PLP card (carousel, video, quick-add, swatches).
     Returns the markup used by both /search and collection pages.
     `ctx`: { currencySymbol, hideSwatches, includeQuickAdd }
     ────────────────────────────────────────────────────────────────── */
  /* ─────────────────────────────────────────────────────────────────
     VP (private sale) pricing — mirrors snippets/price.liquid's vp_label_sale
     branch. Only applies for logged-in customers inside the sale window
     (ctx.vpBadge.show) and only when the product isn't already on a standard
     compare-at sale (`hasComparePrice`), which takes precedence — same badge
     order as product-card.liquid. The discount % is indexed as a named_tag
     keyed by settings.vp_label_prefix (e.g. named_tags.VPSUM26 = "20%").
     Returns { hasVpSale, pct, originalPrice, salePrice }.
     ────────────────────────────────────────────────────────────────── */
  function resolveVpSale(hit, ctx, hasComparePrice) {
    var none = { hasVpSale: false, pct: 0, originalPrice: '', salePrice: '' };
    var rawPrice = parseFloat(hit.price);
    if (!ctx.vpBadge || !ctx.vpBadge.show || !ctx.vpBadge.prefix || hasComparePrice || isNaN(rawPrice)) {
      return none;
    }

    // The discount value lives in named_tags[prefix] (e.g. "20%"). named_tags isn't
    // always in the index's retrievable set, so fall back to scanning the raw tags
    // list for "<prefix>:<value>" (tags is always retrieved — used for swatches too).
    var vpRaw = null;
    if (hit.named_tags && hit.named_tags[ctx.vpBadge.prefix] != null) {
      vpRaw = hit.named_tags[ctx.vpBadge.prefix];
    } else if (hit.tags) {
      var vpTagList = Array.isArray(hit.tags) ? hit.tags : (hit.tags || '').split(',');
      var vpPrefixLower = (ctx.vpBadge.prefix + ':').toLowerCase();
      for (var vti = 0; vti < vpTagList.length; vti++) {
        var vpTag = (vpTagList[vti] || '').trim();
        if (vpTag.toLowerCase().indexOf(vpPrefixLower) === 0) {
          vpRaw = vpTag.slice(ctx.vpBadge.prefix.length + 1);
          break;
        }
      }
    }

    var vpPct = vpRaw != null ? parseFloat(String(vpRaw).replace(/%/g, '').trim()) : NaN;
    if (isNaN(vpPct) || vpPct <= 0) return none;

    // Matches price.liquid: round((100 - vp) / 100, 2), then price * multiplier.
    var vpMultiplier = Math.round(((100 - vpPct) / 100) * 100) / 100;
    return {
      hasVpSale: true,
      pct: vpPct,
      originalPrice: ctx.currencySymbol + rawPrice.toFixed(2),
      salePrice: ctx.currencySymbol + (rawPrice * vpMultiplier).toFixed(2),
    };
  }

  function buildProductCardHtml(hit, ctx) {
    var currencySymbol = ctx.currencySymbol;
    var hideSwatches = !!ctx.hideSwatches;
    var includeQuickAdd = ctx.includeQuickAdd !== false;
    var useUnisexCustomImage = !!ctx.useUnisexCustomImage;

    var lightBg = hit.meta && hit.meta.picture && hit.meta.picture.light_background;
    var isLight = lightBg === 'true' || lightBg === true;
    var lightClass = isLight ? 'ais-hit--product-type-light' : '';

    var price = '';
    if (hit.price != null) {
      var p = parseFloat(hit.price);
      if (!isNaN(p)) price = currencySymbol + p.toFixed(2);
    }

    var comparePrice = '';
    var discount = '';
    if (hit.compare_at_price && parseFloat(hit.compare_at_price) > parseFloat(hit.price)) {
      var cp = parseFloat(hit.compare_at_price);
      comparePrice = currencySymbol + cp.toFixed(2);
      var pct = Math.round((1 - parseFloat(hit.price) / cp) * 100);
      if (pct > 0) discount = '-' + pct + '%';
    }

    // VP (private sale) pricing — mirrors snippets/price.liquid's vp_label_sale branch.
    // Only for logged-in customers in the sale window (ctx.vpBadge.show) and only when
    // the product isn't already on a standard compare-at sale (which takes precedence,
    // same as product-card.liquid's badge order). The discount % is indexed as a
    // named_tag keyed by settings.vp_label_prefix (e.g. named_tags.VPSUM26 = "20%").
    var vpSale = resolveVpSale(hit, ctx, !!comparePrice);
    var hasVpSale = vpSale.hasVpSale;
    var vpPct = vpSale.pct;
    var vpOriginalPrice = vpSale.originalPrice;
    var vpSalePrice = vpSale.salePrice;

    var imageUrl = hit.image || '/assets/no-image.svg';
    var extraImages = (hit.meta && hit.meta.search && hit.meta.search.images) || [];
    var videoUrl = (hit.meta && hit.meta.search && hit.meta.search.video) || null;
    var rawVideoPosition = parseInt(hit.meta && hit.meta.search && hit.meta.search.video_position, 10);
    var videoPosition = !isNaN(rawVideoPosition) && rawVideoPosition > 1 ? rawVideoPosition : 1;
    var productUrl = '/products/' + (hit.handle || '');
    var title = hit.title || '';
    var altText = title.replace(/"/g, '&quot;');

    // On `hombre` collections, the featured media is replaced by the product's
    // `custom.unisex_custom_image` metafield (mirrors main-collection-product-grid.liquid).
    // Algolia indexes the file_reference as a raw Shopify GID, so the section pre-resolves
    // each product's URL via Liquid `| file_url` and passes the map as ctx.unisexCustomImageByHandle.
    var unisexCustomMedia = '';
    if (useUnisexCustomImage && hit.handle && ctx.unisexCustomImageByHandle) {
      unisexCustomMedia = ctx.unisexCustomImageByHandle[hit.handle] || '';
    }
    var unisexCustomIsVideo = unisexCustomMedia && /\.(mp4|webm|mov)(\?|$)/i.test(unisexCustomMedia);

    var isAvailable = hit.inventory_available !== false && hit.available !== false;
    // Ordered media list. hit.image + meta.search.images give the images in order;
    // the video is spliced in at meta.search.video_position (1-based). If position
    // is missing, the video is omitted from the carousel.
    var media;
    if (unisexCustomMedia) {
      // Replaces the featured media (mirrors main-collection-product-grid.liquid).
      // Skip meta.search.video so the unisex media stays at position 0 and the user
      // doesn't see the regular product video autoplay over the override.
      media = [{ type: unisexCustomIsVideo ? 'video' : 'image', url: unisexCustomMedia }];
      media.push({ type: 'image', url: imageUrl });
      for (var ei = 0; ei < extraImages.length; ei++) {
        media.push({ type: 'image', url: extraImages[ei] });
      }
    } else {
      media = [{ type: 'image', url: imageUrl }];
      for (var ei2 = 0; ei2 < extraImages.length; ei2++) {
        media.push({ type: 'image', url: extraImages[ei2] });
      }
      if (videoUrl) {
        var insertAt = Math.min(videoPosition - 1, media.length);
        media.splice(insertAt, 0, { type: 'video', url: videoUrl });
      }
    }
    var hasCarousel = media.length > 1;

    var html =
      '<div class="card-wrapper ais-product-card" data-handle="' +
      (hit.handle || '') +
      '" data-product-id="' +
      (hit.id || '') +
      '" data-available="' + isAvailable + '">';
    html += '<div class="card card--product">';

    // Sale badge (top-left) — mirrors snippets/product-card.liquid's badge order:
    // a standard compare-at markdown takes precedence, otherwise the VP (private
    // sale) badge. settings.sale_badge_color_scheme resolves to `sale-ss25`, the
    // same scheme as the VP badge, so both share the .ais-hit--badge styling.
    if (discount) {
      html += '<span class="ais-hit--badge badge color-sale-ss25">' + discount + '</span>';
    } else if (hasVpSale) {
      html += '<span class="ais-hit--badge badge color-sale-ss25">-' + vpPct + '%</span>';
    }

    if (includeQuickAdd && isAvailable) {
      html += '<button type="button" class="modal-opener modal-action pointer-fine-hide">';
      html += '<div class="button-wrapper">+</div>';
      html += '</button>';
    }

    html += '<a href="' + productUrl + '" style="text-decoration:none;color:inherit;">';
    html += '<div class="ais-hit--gallery' + (isLight ? ' ais-hit--gallery--light' : '') + '">';
    html += '<div class="ais-hit--gallery-container">';
    for (var i = 0; i < media.length; i++) {
      var item = media[i];
      if (item.type === 'video') {
        html += '<div class="ais-hit--slide"><video class="ais-hit--picture ais-hit--video" src="' + item.url + '" autoplay muted loop playsinline poster="' + sizedImageUrl(imageUrl) + '"></video></div>';
      } else if (i === 0) {
        html += '<div class="ais-hit--slide"><img class="ais-hit--picture" src="' + sizedImageUrl(item.url) + '" alt="' + altText + '" loading="lazy" /></div>';
      } else {
        html += '<div class="ais-hit--slide"><img class="ais-hit--picture" data-lazy-src="' + sizedImageUrl(item.url) + '" alt="' + altText + '" /></div>';
      }
    }
    html += '</div>';
    if (hasCarousel) {
      var chevronLeft = '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
      var chevronRight = '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';
      html += '<button type="button" class="ais-hit--nav ais-hit--nav-prev" disabled>' + chevronLeft + '</button>';
      html += '<button type="button" class="ais-hit--nav ais-hit--nav-next">' + chevronRight + '</button>';
    }
    html += '</div>';

    html += '<div class="ais-hit--details' + (isLight ? ' ais-hit--details--light' : '') + '">';
    html += '<p class="ais-hit--title"><a href="' + productUrl + '" class="' + lightClass + '">' + title + '</a></p>';
    html += '<p class="ais-hit--price">';
    if (hasVpSale) {
      // VP private sale: original price struck through, VP-discounted price as the main price.
      html += '<span class="ais-hit--price-striked">' + vpOriginalPrice + '</span>';
      html += '<b>' + vpSalePrice + '</b>';
    } else {
      if (comparePrice) {
        html += '<span class="ais-hit--price-striked">' + comparePrice + '</span>';
      }
      html += '<b>' + price + '</b>';
      if (discount) {
        html += ' <span class="ais-hit--price-discount">' + discount + '</span>';
      }
    }
    html += '</p>';

    if (!hideSwatches && hit.tags) {
      var colorTag = '';
      var tags = Array.isArray(hit.tags) ? hit.tags : (hit.tags || '').split(',');
      for (var ti = 0; ti < tags.length; ti++) {
        var tag = (tags[ti] || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
        if (tag.indexOf('color-') === 0) { colorTag = tag; break; }
      }
      if (colorTag) {
        html += '<div class="ais-hit--colors" data-color-collection="' + colorTag + '"></div>';
      }
    }

    html += '</div>';
    html += '</a></div></div>';

    return html;
  }

  /* ─────────────────────────────────────────────────────────────────
     Market-specific pricing override.
     Localized markets (e.g. USD) carry their presentment price in a
     `market_pricing_<currency>` object (price / compare_at_price) rather than
     the top-level price/compare_at_price, which stay in the index's base
     currency. When such an object exists for the active currency, promote it
     onto the hit so every downstream consumer — card display, cached analytics
     price, VP-sale math — uses the localized amount. No-op for markets whose
     base currency already matches (the object is absent).
     ────────────────────────────────────────────────────────────────── */
  /* Explicit attributesToRetrieve for PLP/search hit queries. Replaces the
     old ['*', ...] which shipped every indexed attribute for all 24 hits per
     page. Covers everything read by buildProductCardHtml, the sections' item
     templates, buildInfiniteHitsTransformItems and click/impression tracking.
     Add here when a template starts reading a new hit field. */
  function hitAttributesToRetrieve(currency) {
    return [
      'objectID',
      'id',
      'handle',
      'title',
      'price',
      'compare_at_price',
      'image',
      'tags',
      'named_tags',
      'available',
      'inventory_available',
      'inventory_policy',
      'locations_inventory',
      'meta.search.images',
      'meta.search.video',
      'meta.search.video_position',
      'meta.picture.light_background',
      'market_pricing_' + String(currency || '').toLowerCase(),
    ];
  }

  function applyMarketPricing(hit, currency) {
    if (!hit || !currency) return hit;
    var mp = hit['market_pricing_' + String(currency).toLowerCase()];
    if (!mp || mp.price == null) return hit;
    hit.price = mp.price;
    hit.compare_at_price =
      mp.compare_at_price != null && parseFloat(mp.compare_at_price) > 0 ? mp.compare_at_price : null;
    return hit;
  }

  /* ─────────────────────────────────────────────────────────────────
     infiniteHits transformItems factory.
       - promotes market_pricing_<currency> to the top-level price fields
       - caches hit data by handle for click/impression tracking
       - on non-primary indices, filters by inventory_available or
         locations_inventory aggregated against fulfillmentLocationIds
       - sets _distinct:false on each item
     ────────────────────────────────────────────────────────────────── */
  function buildInfiniteHitsTransformItems(opts) {
    var isPrimaryIndex = !!opts.isPrimaryIndex;
    var fulfillmentLocationIds = opts.fulfillmentLocationIds || [];
    var hitsByHandle = opts.hitsByHandle;
    var currency = opts.currency;

    return function (items) {
      items.forEach(function (item) {
        applyMarketPricing(item, currency);
        if (item.objectID) {
          hitsByHandle[item.handle] = {
            objectID: item.objectID,
            shopifyProductID: item.id,
            title: item.title || '',
            price: item.price,
            position: typeof item.__position === 'number' ? item.__position : null,
          };
        }
      });
      // Primary index: tags:con_stock (see each section's stockFilter) filters
      // stock server-side, so no client-side inventory pass is needed here.
      if (isPrimaryIndex) {
        return items.map(function (item) {
          return Object.assign({}, item, { _distinct: false });
        });
      }
      return items
        .filter(function (item) {
          if (item.inventory_policy === 'continue') return true;
          var locs = item.locations_inventory;
          if (!locs || typeof locs !== 'object' || !fulfillmentLocationIds.length) {
            return item.inventory_available === true;
          }
          var stock = 0;
          fulfillmentLocationIds.forEach(function (locId) {
            if (locs[locId]) stock += (parseInt(locs[locId], 10) || 0);
          });
          return stock > 0;
        })
        .map(function (item) {
          return Object.assign({}, item, { _distinct: false });
        });
    };
  }

  /* ─────────────────────────────────────────────────────────────────
     Embla carousel init for product card galleries.
     Watches mutations on the hits container and initialises every
     `.ais-hit--gallery` that has 2+ slides, with deterministic
     horizontal-trackpad handling and lazy image loading.
     ────────────────────────────────────────────────────────────────── */
  function setupHitGalleryCarousels(hitsContainer) {
    if (!hitsContainer) return;
    var EmblaCarousel = window.EmblaCarousel;
    if (!EmblaCarousel) return;

    function initGalleries(root) {
      var galleries = (root || document).querySelectorAll('.ais-hit--gallery:not([data-embla-init])');
      galleries.forEach(function (viewport) {
        var slides = viewport.querySelectorAll('.ais-hit--slide');
        if (slides.length < 2) return;

        viewport.setAttribute('data-embla-init', '1');

        var embla = EmblaCarousel(viewport, {
          loop: false,
          dragFree: false,
          containScroll: 'trimSnaps',
          duration: 20,
        });

        var WHEEL_GESTURE_COOLDOWN = 100;
        var WHEEL_GESTURE_QUIET_PERIOD = 80;
        var wheelDeltaX = 0;
        var wheelDirection = 0;
        var wheelGestureLocked = false;
        var wheelDraggingClassTimer = null;
        var wheelUnlockTimer = null;
        var wheelIgnoreUntil = 0;
        var wheelLastEventAt = 0;
        var wheelResumeArmed = false;

        function resetWheelGesture() {
          wheelDeltaX = 0;
          wheelDirection = 0;
          wheelGestureLocked = false;
          viewport.classList.remove('is-wheel-dragging');
        }

        function clearWheelDraggingClassSoon() {
          if (wheelDraggingClassTimer) clearTimeout(wheelDraggingClassTimer);
          wheelDraggingClassTimer = setTimeout(function () {
            viewport.classList.remove('is-wheel-dragging');
          }, 24);
        }

        function scheduleWheelUnlock(delay) {
          if (wheelUnlockTimer) clearTimeout(wheelUnlockTimer);
          wheelUnlockTimer = setTimeout(function () {
            wheelGestureLocked = false;
          }, delay);
        }

        viewport.addEventListener(
          'wheel',
          function (e) {
            if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
            if (Math.abs(e.deltaX) < 2) {
              clearWheelDraggingClassSoon();
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            viewport.classList.add('is-wheel-dragging');

            var now = Date.now();
            var lastEventAt = wheelLastEventAt;
            wheelLastEventAt = now;
            var nextDirection = e.deltaX > 0 ? 1 : -1;

            if (now < wheelIgnoreUntil) {
              wheelResumeArmed = true;
              clearWheelDraggingClassSoon();
              return;
            }
            if (wheelResumeArmed) {
              if (lastEventAt && now - lastEventAt < WHEEL_GESTURE_QUIET_PERIOD) {
                clearWheelDraggingClassSoon();
                return;
              }
              wheelResumeArmed = false;
              wheelDeltaX = 0;
              wheelDirection = 0;
            }
            if (wheelDirection !== nextDirection) {
              wheelDeltaX = 0;
              wheelDirection = nextDirection;
            }

            var canPrev = embla.canScrollPrev();
            var canNext = embla.canScrollNext();
            if ((e.deltaX < 0 && !canPrev) || (e.deltaX > 0 && !canNext)) {
              resetWheelGesture();
              return;
            }
            wheelDeltaX += e.deltaX;
            if (wheelGestureLocked) { clearWheelDraggingClassSoon(); return; }
            if (Math.abs(wheelDeltaX) < 24) { clearWheelDraggingClassSoon(); return; }
            wheelGestureLocked = true;
            wheelIgnoreUntil = now + WHEEL_GESTURE_COOLDOWN;
            if (wheelDeltaX > 0) embla.scrollNext(); else embla.scrollPrev();
            wheelDeltaX = 0;
            wheelResumeArmed = false;
            scheduleWheelUnlock(WHEEL_GESTURE_COOLDOWN);
            clearWheelDraggingClassSoon();
          },
          { passive: false, capture: true }
        );

        var prevBtn = viewport.querySelector('.ais-hit--nav-prev');
        var nextBtn = viewport.querySelector('.ais-hit--nav-next');

        function lazyLoad() {
          var slidesInView = embla.slidesInView();
          var toLoad = slidesInView.slice();
          var selected = embla.selectedScrollSnap();
          if (selected - 1 >= 0) toLoad.push(selected - 1);
          toLoad.push(selected);
          if (selected + 1 < slides.length) toLoad.push(selected + 1);
          if (selected + 2 < slides.length) toLoad.push(selected + 2);
          var uniqueToLoad = [];
          var seen = {};
          toLoad.forEach(function (idx) {
            if (!seen[idx]) { seen[idx] = true; uniqueToLoad.push(idx); }
          });
          uniqueToLoad.forEach(function (idx) {
            var img = slides[idx].querySelector('img[data-lazy-src]');
            if (img) {
              img.src = img.getAttribute('data-lazy-src');
              img.removeAttribute('data-lazy-src');
              img.style.visibility = 'visible';
            }
          });
        }

        function updateButtons() {
          prevBtn.disabled = !embla.canScrollPrev();
          nextBtn.disabled = !embla.canScrollNext();
        }

        prevBtn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); embla.scrollPrev(); });
        nextBtn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); embla.scrollNext(); });

        embla.on('select', updateButtons);
        embla.on('init', updateButtons);
        embla.on('select', lazyLoad);
        embla.on('init', lazyLoad);
        embla.on('settle', function () {
          wheelDeltaX = 0;
          wheelDirection = 0;
          wheelGestureLocked = false;
          wheelLastEventAt = 0;
          wheelResumeArmed = false;
        });

        var linkEl = viewport.closest('a');
        if (linkEl) {
          var startX = 0;
          viewport.addEventListener('pointerdown', function (e) { startX = e.clientX; });
          linkEl.addEventListener('click', function (e) {
            if (Math.abs(e.clientX - startX) > 5) e.preventDefault();
          });
        }
      });
    }

    var obs = new MutationObserver(function () { initGalleries(hitsContainer); });
    obs.observe(hitsContainer, { childList: true, subtree: true });
    initGalleries(hitsContainer);
  }

  /* ─────────────────────────────────────────────────────────────────
     Color swatches: lazy-load from `/collections/{color-handle}/products.json`
     for each `.ais-hit--colors[data-color-collection]` rendered.
     ────────────────────────────────────────────────────────────────── */
  function setupColorSwatchesLazyLoad(opts) {
    var hitsContainer = opts.hitsContainer;
    if (!hitsContainer) return;
    var lastUnitsSuffix = opts.lastUnitsSuffix || '';
    var newSeasonSuffix = opts.newSeasonSuffix || '';
    var isLastUnitsPage = !!opts.isLastUnitsPage;
    var isNewSeasonPage = !!opts.isNewSeasonPage;
    var colorCollectionCache = {};

    function handleize(str) {
      return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    function shouldShowSwatch(productUrl) {
      if (lastUnitsSuffix) {
        return isLastUnitsPage
          ? productUrl.indexOf(lastUnitsSuffix) !== -1
          : productUrl.indexOf(lastUnitsSuffix) === -1;
      }
      if (newSeasonSuffix) {
        if (isNewSeasonPage) return productUrl.indexOf(newSeasonSuffix) !== -1;
        return true;
      }
      return true;
    }

    function renderSwatches(container) {
      var colorHandle = container.getAttribute('data-color-collection');
      if (!colorHandle || container.hasAttribute('data-colors-loaded')) return;
      container.setAttribute('data-colors-loaded', '1');

      function render(products) {
        var html = '';
        var count = 0;
        for (var i = 0; i < products.length; i++) {
          var prod = products[i];
          var hideSwatch = false;
          if (prod.tags) {
            var prodTags = Array.isArray(prod.tags) ? prod.tags : prod.tags.split(',');
            for (var j = 0; j < prodTags.length; j++) {
              if (prodTags[j].trim().toLowerCase() === 'hide_swatch') { hideSwatch = true; break; }
            }
          }
          if (hideSwatch) continue;
          if (!shouldShowSwatch(prod.url || '/products/' + prod.handle)) continue;
          count++;
          if (count > 4) {
            html += '<a href="' + (prod.url || '/products/' + prod.handle) + '" class="full-unstyled-link more-colors"><span> + </span></a>';
            break;
          }
          var color = handleize((prod.variants && prod.variants[0] && prod.variants[0].option1) || '');
          html += '<a href="' + (prod.url || '/products/' + prod.handle) + '" class="card-product__color color-' + color + '" title="' + color + '"></a>';
        }
        if (html) container.innerHTML = html;
      }

      if (colorCollectionCache[colorHandle]) { render(colorCollectionCache[colorHandle]); return; }
      fetch('/collections/' + colorHandle + '/products.json?limit=10')
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var products = data.products || [];
          colorCollectionCache[colorHandle] = products;
          render(products);
        })
        .catch(function () {});
    }

    var obs = new MutationObserver(function () {
      var containers = hitsContainer.querySelectorAll('.ais-hit--colors:not([data-colors-loaded])');
      containers.forEach(function (c) { renderSwatches(c); });
    });
    obs.observe(hitsContainer, { childList: true, subtree: true });
  }

  /* ─────────────────────────────────────────────────────────────────
     Quick-add: lazy-fetch variant data from /products/{handle}.js on
     first hover/click, then re-dispatch the modal-opener click so
     CardQuickSizes can handle it. Also fires `algolia_product_clicked`
     for the analytics pixel and calls `onProductClick(card)` (used by
     scroll-restore to persist position).
     ────────────────────────────────────────────────────────────────── */
  function setupQuickAdd(opts) {
    var hitsContainer = opts.hitsContainer;
    if (!hitsContainer) return;
    var lastHitsByHandle = opts.lastHitsByHandle || {};
    var clickEventPayloadExtras = opts.clickEventPayloadExtras || {};
    var onProductClick = opts.onProductClick || function () {};

    var productDataCache = {};
    var hoverListenersPatched = false;

    function ensureProductData(cardEl) {
      var handle = cardEl.dataset.handle;
      if (!handle || cardEl.classList.contains('product-card-wrapper')) {
        return Promise.resolve();
      }
      if (productDataCache[handle]) {
        applyProductData(cardEl, productDataCache[handle]);
        return Promise.resolve();
      }
      return fetch('/products/' + handle + '.js')
        .then(function (res) { return res.json(); })
        .then(function (product) {
          var rawOpts = product.options || [];
          var options = rawOpts.map(function (opt, idx) {
            if (typeof opt === 'object' && opt !== null) {
              return { name: opt.name, position: opt.position || (idx + 1), values: opt.values || [] };
            }
            var position = idx + 1;
            var values = [];
            var seen = {};
            product.variants.forEach(function (v) {
              var val = v['option' + position];
              if (val != null && !seen[val]) { seen[val] = true; values.push(val); }
            });
            return { name: opt, position: position, values: values };
          });
          var data = {
            variants: product.variants.map(function (v) {
              return { id: v.id, title: v.title, available: v.available, option1: v.option1, option2: v.option2, option3: v.option3 };
            }),
            options: options,
            available: product.available,
          };
          productDataCache[handle] = data;
          applyProductData(cardEl, data);
        })
        .catch(function (err) { console.error('[quick-add] Failed to load product data for', handle, err); });
    }

    function applyProductData(cardEl, data) {
      cardEl.dataset.variants = JSON.stringify(data.variants);
      cardEl.dataset.options = JSON.stringify(data.options);
      cardEl.dataset.available = String(data.available);
      cardEl.classList.add('product-card-wrapper');
      if (!hoverListenersPatched) {
        hoverListenersPatched = true;
        var cardSizesEl = document.querySelector('card-sizes');
        if (cardSizesEl) {
          var firstBtn = document.querySelector('.product-card-wrapper .modal-opener');
          if (firstBtn && window.getComputedStyle(firstBtn).getPropertyValue('display') === 'none') {
            document.addEventListener('mouseover', cardSizesEl.onHover.bind(cardSizesEl));
            document.addEventListener('mouseout', cardSizesEl.onMouseOut.bind(cardSizesEl));
          }
        }
      }
    }

    hitsContainer.addEventListener('click', function (e) {
      var productLink = e.target.closest('.ais-product-card a[href*="/products/"]');
      if (!productLink) return;
      var card = productLink.closest('.ais-product-card');
      onProductClick(card);
      var handle = card && card.dataset.handle;
      var hitData = handle && lastHitsByHandle[handle];
      if (hitData && window.Shopify && window.Shopify.analytics) {
        var payload = {
          product_id: hitData.shopifyProductID,
          product_name: hitData.title,
          product_price: hitData.price,
          index: hitData.position,
        };
        Object.keys(clickEventPayloadExtras).forEach(function (k) { payload[k] = clickEventPayloadExtras[k]; });
        Shopify.analytics.publish('algolia_product_clicked', payload);
      }
    }, true);

    hitsContainer.addEventListener('mouseover', function (e) {
      var card = e.target.closest('.ais-product-card');
      if (card && !card.classList.contains('product-card-wrapper') && !card._qsFetching) {
        card._qsFetching = true;
        ensureProductData(card).then(function () {
          // If the cursor is still over the card after data arrives, re-fire
          // mouseover so card-sizes' onHover (registered on document) picks it up.
          // Without this, the user has to hover a second time on first load.
          if (card.matches(':hover')) {
            card.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, cancelable: true }));
          }
        });
      }
    });

    hitsContainer.addEventListener('click', function (e) {
      var opener = e.target.closest('.modal-opener');
      if (!opener) return;
      var card = opener.closest('.ais-product-card');
      if (!card) return;
      if (card.classList.contains('product-card-wrapper')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      opener.classList.add('is-loading');
      ensureProductData(card).then(function () {
        opener.classList.remove('is-loading');
        opener.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      });
    }, true);
  }

  /* ─────────────────────────────────────────────────────────────────
     PLP scroll-position restore on back/forward navigation.
     ────────────────────────────────────────────────────────────────── */
  function setupPlpScrollRestore(opts) {
    var storageKey = opts.storageKey || 'algolia-plp-restore-state';
    var navEntries = (window.performance && typeof window.performance.getEntriesByType === 'function')
      ? window.performance.getEntriesByType('navigation') : [];
    var navigationType = navEntries.length ? navEntries[0].type : '';
    var shouldRestore = navigationType === 'back_forward';
    var hasRestored = false;

    function read() {
      try { var raw = sessionStorage.getItem(storageKey); return raw ? JSON.parse(raw) : null; }
      catch (e) { return null; }
    }
    function clear() { try { sessionStorage.removeItem(storageKey); } catch (e) {} }

    function persistFromCard(cardEl) {
      if (!cardEl) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          path: window.location.pathname,
          handle: cardEl.dataset.handle || '',
          scrollY: window.scrollY || window.pageYOffset || 0,
          timestamp: Date.now(),
        }));
      } catch (e) {}
    }

    function restore() {
      if (!shouldRestore || hasRestored) return;
      var stored = read();
      if (!stored || stored.path !== window.location.pathname) return;
      if (!stored.handle || Date.now() - stored.timestamp > 30 * 60 * 1000) { clear(); return; }
      var card = document.querySelector('.ais-product-card[data-handle="' + stored.handle + '"]');
      if (!card) return;
      hasRestored = true;
      requestAnimationFrame(function () {
        window.scrollTo(0, stored.scrollY || 0);
        setTimeout(function () { window.scrollTo(0, stored.scrollY || 0); clear(); }, 120);
      });
    }

    return { persistFromCard: persistFromCard, restore: restore };
  }

  /* ─────────────────────────────────────────────────────────────────
     Infinite scroll: arm autoload only after first hit list paints,
     keep a single observed load-more button, guard against duplicate
     requests, and re-observe after each render.
     ────────────────────────────────────────────────────────────────── */
  function setupInfiniteScroll(opts) {
    var search = opts.search;
    var loadMoreSelector = opts.loadMoreSelector;
    var sectionSelector = opts.sectionSelector;
    var hitListItemSelector = opts.hitListItemSelector || '.ais-InfiniteHits-list .ais-InfiniteHits-item';
    var onRender = opts.onRender || function () {};
    var hitsContainer = opts.hitsContainer;
    var impressionTracker = opts.impressionTracker;

    var armed = false;
    var requestInFlight = false;
    var canAutoload = true;
    var observedBtn = null;

    function isClickable(btn) {
      return (
        btn &&
        !btn.disabled &&
        !btn.classList.contains('ais-InfiniteHits-loadMore--disabled') &&
        btn.offsetParent !== null
      );
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var btn = entry.target;
          if (!entry.isIntersecting) {
            if (!requestInFlight) canAutoload = true;
            return;
          }
          if (!armed || requestInFlight || !canAutoload || !isClickable(btn)) return;
          observer.unobserve(btn);
          observedBtn = null;
          requestInFlight = true;
          canAutoload = false;
          btn.click();
        });
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );

    function observeBtn(btn) {
      if (!armed || requestInFlight || !isClickable(btn)) return;
      if (observedBtn === btn) return;
      if (observedBtn) observer.unobserve(observedBtn);
      observer.observe(btn);
      observedBtn = btn;
    }

    function scheduleArm() {
      if (armed) return;
      var section = document.querySelector(sectionSelector);
      if (!section || !section.querySelector(hitListItemSelector)) return;
      armed = true;
      setTimeout(function () {
        var btn = document.querySelector(loadMoreSelector);
        if (btn) observeBtn(btn);
      }, 450);
    }

    if (search && typeof search.on === 'function') {
      search.on('render', function () {
        if (requestInFlight) requestInFlight = false;
        scheduleArm();
        onRender();
        if (armed) {
          setTimeout(function () {
            var btn = document.querySelector(loadMoreSelector);
            if (btn) observeBtn(btn);
          }, 150);
        }
      });
    }

    if (hitsContainer) {
      var mut = new MutationObserver(function () {
        if (impressionTracker && typeof impressionTracker.scheduleObserve === 'function') {
          impressionTracker.scheduleObserve();
        }
        if (!armed || requestInFlight) return;
        var btn = document.querySelector(loadMoreSelector);
        if (btn && isClickable(btn) && btn !== observedBtn) observeBtn(btn);
      });
      mut.observe(hitsContainer, { childList: true, subtree: true });
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     Common widget builders.
     ────────────────────────────────────────────────────────────────── */
  function buildClearRefinementsWidget(instantsearch, t) {
    return instantsearch.widgets.clearRefinements({
      container: '#ais-clear-refinements',
      templates: { resetLabel: function () { return t.clearRefinements; } },
    });
  }

  function buildCurrentRefinementsWidget(instantsearch) {
    return instantsearch.widgets.currentRefinements({
      container: '#ais-current-refinements',
    });
  }

  /* ─────────────────────────────────────────────────────────────────
     Search-query → URL sync as a custom widget. Returns the widget
     plus a getSearchParameters wrapper consumers wire into their own
     dynamicFiltersWidget. Used by both sections in their dynamic
     filters widget.
     ────────────────────────────────────────────────────────────────── */
  function syncQueryToUrl(helperState) {
    var query = (helperState.query || '').trim();
    var url = new URL(window.location);
    if (query) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    window.history.replaceState({}, '', url);
  }

  /**
   * InstantSearch middleware: logs to the console every time a facet refinement
   * changes. Diffs the helper's disjunctive/hierarchical/numeric refinements
   * across renders, so it catches refinements from every widget (including the
   * color refinementList, whose custom `templates.item` bypasses sendEvent).
   *
   * Wire via `search.use(window.AlgoliaShared.createFilterLogMiddleware())`
   * before `search.start()`.
   */
  function createFilterLogMiddleware() {
    function getCollectionHandle() {
      var path = (window.location.pathname || '').replace(/\/+$/, '');
      return path.substring(path.lastIndexOf('/') + 1);
    }
    return function (params) {
      var instantSearchInstance = params.instantSearchInstance;
      var previous = {};

      function snapshotRefinements(state) {
        var snap = {};
        var disj = state.disjunctiveFacetsRefinements || {};
        Object.keys(disj).forEach(function (attr) {
          (disj[attr] || []).forEach(function (v) { snap[attr + ':' + v] = { attribute: attr, value: v }; });
        });
        var conj = state.facetsRefinements || {};
        Object.keys(conj).forEach(function (attr) {
          (conj[attr] || []).forEach(function (v) { snap[attr + ':' + v] = { attribute: attr, value: v }; });
        });
        return snap;
      }

      function lastWord(attr) {
        return String(attr || '').split('.').pop();
      }

      function diffAndLog(state) {
        var current = snapshotRefinements(state);
        Object.keys(current).forEach(function (key) {
          if (!previous[key]) {
            var attr = lastWord(current[key].attribute);
            var value = current[key].value;
            var collectionHandle = getCollectionHandle();
            console.log('[Algolia] Filter applied', attr, value, collectionHandle);
            if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
              window.Shopify.analytics.publish('algolia_filter_applied', { attribute: attr, value: value, collection_handle: collectionHandle });
            }
          }
        });
        Object.keys(previous).forEach(function (key) {
          if (!current[key]) console.log('[Algolia] Filter removed', lastWord(previous[key].attribute), previous[key].value);
        });
        previous = current;
      }

      var onChange = function () { diffAndLog(instantSearchInstance.helper.state); };

      return {
        subscribe: function () {
          var attach = function () {
            if (!instantSearchInstance.helper) { setTimeout(attach, 50); return; }
            previous = snapshotRefinements(instantSearchInstance.helper.state);
            instantSearchInstance.helper.on('change', onChange);
          };
          attach();
        },
        onStateChange: function () {},
        unsubscribe: function () {
          if (instantSearchInstance.helper) {
            instantSearchInstance.helper.removeListener('change', onChange);
          }
        },
      };
    };
  }

  /**
   * InstantSearch `onStateChange` that makes the product-type facet
   * (FILTER_BAR_ATTRIBUTE) single-select: picking a second type replaces the
   * first instead of OR-ing with it (jackets, then coats → coats only). Covers
   * the filter bar and the drawer, which share the attribute. Every other facet
   * stays multi-select (black + blue). The helper appends refinements in click
   * order, so the last value is the one just picked.
   *
   * Wire via `instantsearch({ ..., onStateChange: window.AlgoliaShared.singleValueFacetsOnStateChange })`.
   */
  function singleValueFacetsOnStateChange(params) {
    var uiState = params.uiState;
    var next = {};
    Object.keys(uiState).forEach(function (indexId) {
      var indexState = uiState[indexId] || {};
      var lists = indexState.refinementList;
      var values = lists && lists[FILTER_BAR_ATTRIBUTE];
      if (!values || values.length < 2) {
        next[indexId] = indexState;
        return;
      }
      var trimmed = Object.assign({}, lists);
      trimmed[FILTER_BAR_ATTRIBUTE] = values.slice(-1);
      next[indexId] = Object.assign({}, indexState, { refinementList: trimmed });
    });
    params.setUiState(next);
  }

  window.AlgoliaShared = {
    singleValueFacetsOnStateChange: singleValueFacetsOnStateChange,
    getCurrencySymbol: getCurrencySymbol,
    sizedImageUrl: sizedImageUrl,
    hitAttributesToRetrieve: hitAttributesToRetrieve,
    createFilterLogMiddleware: createFilterLogMiddleware,
    bestSellerFilter: bestSellerFilter,
    createImpressionTracker: createImpressionTracker,
    setupFacetsPanel: setupFacetsPanel,
    setupCollapsibleFacets: setupCollapsibleFacets,
    setupWheelScrollPrevention: setupWheelScrollPrevention,
    buildFallbackFacetWidget: buildFallbackFacetWidget,
    buildSortByWidget: buildSortByWidget,
    buildFilterBar: buildFilterBar,
    buildFacetOmitter: buildFacetOmitter,
    buildProductCardHtml: buildProductCardHtml,
    resolveVpSale: resolveVpSale,
    applyMarketPricing: applyMarketPricing,
    buildInfiniteHitsTransformItems: buildInfiniteHitsTransformItems,
    setupHitGalleryCarousels: setupHitGalleryCarousels,
    setupColorSwatchesLazyLoad: setupColorSwatchesLazyLoad,
    setupQuickAdd: setupQuickAdd,
    setupPlpScrollRestore: setupPlpScrollRestore,
    setupInfiniteScroll: setupInfiniteScroll,
    buildClearRefinementsWidget: buildClearRefinementsWidget,
    buildCurrentRefinementsWidget: buildCurrentRefinementsWidget,
    syncQueryToUrl: syncQueryToUrl,
  };
})();
